import { observer } from "mobx-react-lite";
import { type FC, useEffect, useState } from "react";
import type { Control } from "react-hook-form";

import {
  PROPERTY_ICON_ACCEPTED_TYPES,
  PropertyIconValidation,
} from "@/api/admin/properties";
import {
  ErrorForm,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import { MediaStore, MediaUploader, UploadedFiles } from "@/components/units";
import DynamicTable from "@/components/units/Tables/DynamicTable";
import type { ColumnConfig } from "@/components/units/Tables/type";
import type { MappedProperty } from "@/modules/SystemTags/components/mapProperty";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import { InputControl } from "../InputControl";
import type { NewTagFormType } from "./types";

interface IconCellProps {
  row: MappedProperty;
  mediaStore: MediaStore;
}

const IconCell: FC<IconCellProps> = observer(({ row, mediaStore }) => {
  const [errorMedia, setErrorMedia] = useState<string | null>(null);

  useEffect(() => {
    if (row.imageUrl) {
      mediaStore.restoreFromPayload([
        {
          id: row.imageUrl,
          previewUrl: row.imageUrl,
          type: "image/svg+xml",
        },
      ]);
    }
  }, [row.imageUrl, mediaStore]);

  return (
    <div className="flex gap-2">
      {mediaStore.mediaFiles.map((media, index) => (
        <UploadedFiles
          key={index}
          media={media}
          index={index}
          mediaStore={mediaStore}
          classees="w-[64px] h-[64px]"
        />
      ))}

      <MediaUploader
        acceptedTypes={PROPERTY_ICON_ACCEPTED_TYPES}
        sizeLimits={{
          max: PropertyIconValidation.vector?.maxSize,
          min: PropertyIconValidation.vector?.minSize,
        }}
        disabled={mediaStore.isImageLimitReached}
        mediaStore={mediaStore}
        onError={setErrorMedia}
        classes="w-[64px]! h-[64px]!"
      />

      {errorMedia && <ErrorForm message={errorMedia} />}
    </div>
  );
});

enum Column {
  ICON = "Icon",
  NAME_ENG = "Tag name (English)",
  NAME_AR = "Tag name (Arabic)",
}

const TABLE_HEADERS: TableHeaderType = Object.values(Column);

interface TransactionsTableProps {
  data: MappedProperty[];
  control: Control<NewTagFormType>;
  getMediaStore: (rowId: string) => MediaStore;
}

const cellClasses = "px-2 sm:px-4";

const TranslationTable: FC<TransactionsTableProps> = ({
  control,
  data,
  getMediaStore,
}) => {
  const { flags } = useVisibleFlags(TABLE_HEADERS);

  const columns: ColumnConfig<MappedProperty>[] = [
    {
      key: "imageUrl",
      label: Column.ICON,
      visible: flags[Column.ICON],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom classes={cellClasses}>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        const mediaStore = getMediaStore(row.id);

        return (
          <TableCellText last={isLast} classes={cellClasses}>
            <IconCell row={row} mediaStore={mediaStore} />
          </TableCellText>
        );
      },
    },
    {
      key: "nameEn",
      label: Column.NAME_ENG,
      visible: flags[Column.NAME_ENG],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom classes={cellClasses}>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes={cellClasses}>
          <InputControl<NewTagFormType>
            formControl={control}
            nameField={`rows.${row.index}.nameEn`}
            data-testid="new-tag-name-input-english"
            classedName="mb-2!"
          />
        </TableCellText>
      ),
    },
    {
      key: "nameAr",
      label: Column.NAME_AR,
      visible: flags[Column.NAME_AR],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom classes={cellClasses}>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast} classes={cellClasses}>
          <InputControl<NewTagFormType>
            formControl={control}
            nameField={`rows.${row.index}.nameAr`}
            data-testid="new-tag-name-input-arabic"
            classedName="mb-2!"
            dir="rtl"
          />
        </TableCellText>
      ),
    },
  ];

  return (
    <DynamicTable<MappedProperty>
      data={data}
      columns={columns}
      isPagination={false}
      tableClasses="mb-6"
    />
  );
};

export default TranslationTable;
